import React from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import LoadingScreen from "components/LoadingScreen";
import useCancelClientReservation from "components/Reservation/useCancelClientReservation";
import ReservationHeader from "components/Reservation/ReservationHeader";
import ReservationCancelled from "components/Reservation/ReservationCancelled";
import ReservationDetails from "components/Reservation/ReservationDetails";
import ReservationExpired from "components/Reservation/ReservationExpired";
import ReservationUsed from "components/Reservation/ReservationUsed";
import PageNav from "components/PageNav/PageNav";
import { Reservation } from "@types";
import {
  DeliveryFlowStep,
  DELIVERY_FLOW_STEP,
  getDeliveryFlowTabs,
} from "components/Delivery/ScheduleDelivery/deliveryFlow";
import { isClientInformationComplete } from "components/Delivery/ScheduleDelivery/ScheduleDeliveryTabs/ClientInformationForm/validationSchema";
import ClientOrderQuery from "pages/Delivery/ClientOrderQuery.gql";
import ClientReservationQuery from "./ClientReservationQuery.gql";
import ClientPortalConfigQuery from "pages/Delivery/ClientPortalConfigQuery.gql";

const Reservations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("xct") || "";
  // When the user clicked a tab to get here, don't auto-redirect away
  const cameFromTab = (location.state as any)?.fromTab === true;

  const { loading, data, error, refetch } = useQuery(ClientReservationQuery, {
    fetchPolicy: "network-only",
  });
  const { cancelClientReservation, loading: cancelLoading } =
    useCancelClientReservation();

  const { loading: configLoading, data: configData } = useQuery(
    ClientPortalConfigQuery,
    { fetchPolicy: "network-only" },
  );

  const tokenState = configData?.clientPortalConfig?.tokenState;
  const contractorConfig = configData?.clientPortalConfig?.contractorConfig;
  const hasOrder = tokenState?.hasOrder ?? false;

  const { loading: orderLoading, data: orderData } = useQuery(
    ClientOrderQuery,
    {
      fetchPolicy: "network-only",
      skip: !hasOrder,
    },
  );

  const handleExpire = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const [reservationState, setReservationState] = React.useState(
    data?.clientReservation ?? null,
  );

  React.useEffect(() => {
    setReservationState(data?.clientReservation ?? null);
  }, [data]);

  const handleCancelReservation = React.useCallback(async () => {
    const updatedReservation = await cancelClientReservation();
    setReservationState(updatedReservation);
  }, [cancelClientReservation]);

  const handleReservationUpdate = React.useCallback(
    (updatedReservation: Reservation) => {
      setReservationState(updatedReservation);
    },
    [],
  );

  const isCoordonneesComplete = React.useMemo(() => {
    if (!hasOrder || !orderData?.clientOrder?.client) {
      return false;
    }

    return isClientInformationComplete(orderData.clientOrder.client);
  }, [hasOrder, orderData]);

  const navTabs = React.useMemo(
    () =>
      getDeliveryFlowTabs({
        hasReservation: true,
        hasOrder,
        isCoordonneesComplete,
      }),
    [hasOrder, isCoordonneesComplete],
  );

  const reservationAreaLabel = React.useMemo(() => {
    const postalCode =
      data?.clientReservation?.postal_code ||
      orderData?.clientOrder?.client?.postal_code;
    const city =
      data?.clientReservation?.city || orderData?.clientOrder?.client?.city;

    if (postalCode && city) {
      return `${postalCode} - ${city}`;
    }

    return postalCode || city || "";
  }, [data, orderData]);

  // Auto-redirect to the flattened recap step only when the reservation is used
  // and the token actually has an order linked. Both queries must have finished
  // so that we never bounce the user to a page that will immediately bounce them back.
  const isFullyLoaded =
    !loading && !configLoading && (!hasOrder || !orderLoading);
  React.useEffect(() => {
    if (
      isFullyLoaded &&
      data?.clientReservation?.status === "used" &&
      tokenState?.hasOrder === true &&
      !cameFromTab &&
      token
    ) {
      navigate(`/?xct=${encodeURIComponent(token)}`, {
        replace: true,
        state: { deliveryStep: DELIVERY_FLOW_STEP.recap },
      });
    }
  }, [isFullyLoaded, data, tokenState, cameFromTab, token, navigate]);

  const handleReservationSaveSuccess = React.useCallback(() => {
    const nextTab = navTabs.find(
      (tab) => tab.id !== DELIVERY_FLOW_STEP.reservation && !tab.disabled,
    );

    if (!nextTab || !token) {
      return;
    }

    navigate(`/?xct=${encodeURIComponent(token)}`, {
      state: { deliveryStep: nextTab.id },
    });
  }, [navTabs, navigate, token]);

  if (loading || configLoading || (hasOrder && orderLoading) || !data) {
    return <LoadingScreen />;
  }

  if (error || !data.clientReservation) {
    return (
      <div className="layout">
        <div className="layout__content">
          <div className="reservation-page">
            <div className="reservation-error">
              <div className="reservation-error__icon-wrapper">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="#a0aec0">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Réservation introuvable</h2>
              <p>
                Le lien utilisé n{"'"}est pas valide ou la réservation n{"'"}
                existe plus.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const reservation = reservationState || data.clientReservation;

  const handleTabChange = (step: DeliveryFlowStep) => {
    if (step === DELIVERY_FLOW_STEP.reservation) {
      return;
    }

    navigate(`/?xct=${encodeURIComponent(token)}`, {
      state: { deliveryStep: step },
    });
  };

  return (
    <div className="layout">
      <div className="layout__header">
        <ReservationHeader
          contractorName={reservation.contractor_name}
          contractorLogoUrl={reservation.contractor_logo_url}
          date={reservation.date}
          halfday={reservation.halfday}
        />
      </div>
      <PageNav
        tabs={navTabs}
        activeTab={DELIVERY_FLOW_STEP.reservation}
        onTabChange={(tab) => handleTabChange(tab.id as DeliveryFlowStep)}
      />
      <div className="layout__content">
        <div className="reservation-page">
          {reservation.status === "expired" ? (
            <ReservationExpired />
          ) : reservation.status === "cancelled" ? (
            <ReservationCancelled />
          ) : reservation.status === "used" ? (
            <ReservationUsed legacy={true} reservation={reservation} />
          ) : (
            <ReservationDetails
              reservation={reservation}
              contractorConfig={contractorConfig}
              onExpire={handleExpire}
              onReservationUpdate={handleReservationUpdate}
              onSaveSuccess={handleReservationSaveSuccess}
              onCancelReservation={handleCancelReservation}
              isCancellingReservation={cancelLoading}
            />
          )}
        </div>
      </div>
      {reservationAreaLabel && (
        <div className="layout__footer">
          <div className="reservation-page">
            <div className="reservation-footer">
              <svg
                className="reservation-footer__icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>{reservationAreaLabel}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reservations;
